const enabled = () => process.env.REACT_APP_UNME_FALLBACK_ENABLED === '1';
const scopeKey = (domain) => `unme-payment-login-${domain}`;
const orderKey = (domain) => `unme-payment-pending-${domain}`;
const validDomain = (domain) => domain === 'cp' || domain === 'terminal';

// This opaque marker is not authentication: the order-status API checks the
// real server session. Rotating it prevents a new login from resuming old UI.
function fallbackScope(value) {
  // This is only an opaque browser marker, never an authorization credential.
  // Server-side ownership checks are mandatory even if two markers collide.
  const seeds = [2166136261, 2246822519, 3266489917, 668265263];
  return seeds.map((seed) => {
    let hash = seed;
    for (let i = 0; i < value.length; i += 1) hash = Math.imul(hash ^ value.charCodeAt(i), 16777619) >>> 0;
    return hash.toString(16).padStart(8, '0');
  }).join('');
}

export async function rotateUnmePaymentLoginScope(domain, loginId) {
  if (!enabled() || !validDomain(domain)) return;
  try {
    // Stable per login ID so reauthentication can still recover its own order.
    // The server nevertheless verifies the actual requester and CP.
    if (!loginId) throw new Error('Missing login ID');
    const value = `${domain}:${loginId}`;
    let scope = fallbackScope(value);
    if (window.crypto?.subtle && typeof TextEncoder !== 'undefined' && loginId) {
      const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
      scope = Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
    }
    window.localStorage.setItem(scopeKey(domain), scope);
    if (window.localStorage.getItem(scopeKey(domain)) !== scope) throw new Error('Storage unavailable');
  } catch (_) { throw new Error('결제 이용을 위한 로그인 정보를 확인할 수 없습니다. 브라우저 저장 설정을 확인해 주세요.'); }
}

export function getUnmePaymentLoginScope(domain) {
  if (!validDomain(domain)) throw new Error('Invalid payment domain');
  const scope = window.localStorage.getItem(scopeKey(domain));
  if (!scope) throw new Error('Login required');
  if (window.localStorage.getItem(scopeKey(domain)) !== scope) throw new Error('Storage unavailable');
  return scope;
}

export function readPendingUnmeOrder(domain, scope) {
  const raw = window.sessionStorage.getItem(orderKey(domain));
  if (!raw) return null;
  let value;
  try { value = JSON.parse(raw); } catch (_) { throw new Error('Invalid recovery record'); }
  if (!value || typeof value.scope !== 'string' || !/^[a-zA-Z0-9._:-]{1,64}$/.test(value.order || '')) throw new Error('Invalid recovery record');
  if (value.scope !== scope) {
    // Never query or discard a previous login's unresolved order.
    throw new Error('Login changed');
  }
  return value.order;
}

export function savePendingUnmeOrder(domain, scope, order) {
  if (!/^[a-zA-Z0-9._:-]{1,64}$/.test(order || '')) throw new Error('Invalid order number');
  if (getUnmePaymentLoginScope(domain) !== scope) throw new Error('Login changed');
  const existing = readPendingUnmeOrder(domain, scope);
  if (existing) throw new Error('An order is already pending');
  const record = JSON.stringify({ scope, order });
  window.sessionStorage.setItem(orderKey(domain), record);
  if (window.sessionStorage.getItem(orderKey(domain)) !== record) throw new Error('Storage unavailable');
}

export function clearPendingUnmeOrder(domain, scope, order) {
  if (getUnmePaymentLoginScope(domain) !== scope) throw new Error('Login changed');
  if (readPendingUnmeOrder(domain, scope) !== order) throw new Error('Pending order missing');
  window.sessionStorage.removeItem(orderKey(domain));
  if (window.sessionStorage.getItem(orderKey(domain)) != null) throw new Error('Storage unavailable');
}
